import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import api from '../api/axios';
import 'react-datepicker/dist/react-datepicker.css';
import * as moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';

import {
    ArrowDownTrayIcon,
    FunnelIcon
} from '@heroicons/react/24/outline';
import { formatMinutes } from '../utils';
import SearchableSelect from '../components/SearchableSelect';
import Table from '../components/Table';

export default function MonthlyAssociateSummary() {
    const today = new Date();
    const defaultEnd = new Date(today.getFullYear(), today.getMonth(), 1);
    const defaultStart = new Date(defaultEnd);
    defaultStart.setMonth(defaultStart.getMonth() - 1);

    const { t } = useTranslation();

    const [dateRange, setDateRange] = useState([defaultStart, defaultEnd]);
    const [startDate, setStartDate] = useState(defaultStart);
    const [endDate, setEndDate] = useState(defaultEnd);
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState(null);
    const [companyId, setCompanyId] = useState(null);

    const handleDateChange = (update) => {
        let [start, end] = update;

        if (start && end) {
            const maxEnd = new Date(start);
            maxEnd.setMonth(start.getMonth() + 1);

            end = start;
        }

        setDateRange([start, end]);
    };

    // User and Role picker
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState(''); // empty = all
    const [selectedRoleId, setSelectedRoleId] = useState(''); // empty = all

    const maxRangeMonths = 12;

    // Export Excel
    const handleExportExcel = async () => {
        if (!startDate || !endDate || !companyId) return;

        try {
            setExporting(true);
            const lang = localStorage.getItem('language') || 'en';
            const start = moment.tz(startDate, 'Asia/Tashkent').startOf('month').utc().toISOString();
            const end = moment.tz(endDate, 'Asia/Tashkent').endOf('month').utc().toISOString();

            let url = `/metrics/${companyId}/associateMonthly/export?startDate=${start}&endDate=${end}&lang=${lang}`;
            if (selectedRoleId) {
                url += `&roleId=${encodeURIComponent(selectedRoleId)}`;
            }
            if (selectedUserId) {
                url += `&userId=${encodeURIComponent(selectedUserId)}`;
            }

            const response = await api.get(url, { responseType: 'blob' });

            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });

            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `monthly_summary_${moment().format('YYYYMM')}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setExporting(false);
        } catch (err) {
            alert('Export failed');
            setExporting(false);
        }
    };

    // Load company + users
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const cid = localStorage.getItem("companyId");
                if (!cid) throw new Error('Company ID not found');
                setCompanyId(cid);

                const usersRes = await api.get(`/companies/${cid}/users`);
                setUsers(usersRes.data?.data || []);

                const rolesRes = await api.get(`/companies/${cid}/roles`);
                setRoles(rolesRes.data?.data || []);
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to load company/users');
            }
        };
        fetchInitialData();
    }, []);

    // Sync date range
    useEffect(() => {
        if (dateRange[0]) setStartDate(dateRange[0]);
        if (dateRange[1]) setEndDate(dateRange[1]);
    }, [dateRange]);

    const fetchMonthlyData = async () => {
        if (!companyId || !startDate || !endDate) return;

        const diffMonths = moment(endDate).diff(moment(startDate), 'months') + 1;
        if (diffMonths > maxRangeMonths) {
            setError(`Maximum range is ${maxRangeMonths} months`);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const start = moment.tz(startDate, 'Asia/Tashkent').startOf('month').utc().toISOString();
            const end = moment.tz(endDate, 'Asia/Tashkent').endOf('month').utc().toISOString();

            let url = `/metrics/${companyId}/associateMonthly?startDate=${start}&endDate=${end}`;
            if (selectedRoleId) {
                url += `&roleId=${encodeURIComponent(selectedRoleId)}`;
            }
            if (selectedUserId) {
                url += `&userId=${encodeURIComponent(selectedUserId)}`;
            }

            const res = await api.get(url);
            const dataList = res.data?.data || [];

            // Sort by date descending (newest first)
            dataList.sort((a, b) => new Date(b.DateLocal) - new Date(a.DateLocal));

            setRecords(dataList);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load monthly summary');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (companyId) {
            fetchMonthlyData();
        }
    }, [companyId]);

    const handleApply = () => {
        fetchMonthlyData();
    };

    return (
        <div className="p-6 text-white min-h-screen bg-gray-900 space-y-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-5">
                <h1 className="text-2xl sm:text-3xl font-bold text-blue-400">{t('monthlyAssociateSummary')}</h1>

                <div className="flex flex-wrap items-end gap-4 w-full sm:w-auto">
                    <div className="w-full sm:w-auto">
                        <label className="block text-sm text-gray-400 mb-1">{t('role')}</label>
                        <select
                            value={selectedRoleId}
                            onChange={(e) => {
                                setSelectedRoleId(e.target.value);
                                setSelectedUserId(''); // reset user if role changes
                            }}
                            className="bg-gray-800 border border-gray-700 rounded px-4 py-2.5 text-white w-full sm:w-52"
                        >
                            <option value="">{t('allRoles')}</option>
                            {roles.map((role) => (
                                <option key={role._id} value={role._id}>
                                    {role.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="w-full sm:w-auto">
                        <label className="block text-sm text-gray-400 mb-1">{t('user')}</label>
                        <SearchableSelect
                            value={selectedUserId}
                            onChange={setSelectedUserId}
                            placeholder={t('allUsers')}
                            options={users
                                .filter(u => !selectedRoleId || (u.role && (u.role._id === selectedRoleId || u.role === selectedRoleId)))
                                .map(u => ({ value: u.systemId, label: `${u.systemId} - ${u.firstName || ''} ${u.lastName || ''}`.trim() }))
                            }
                        />
                    </div>

                    <div style={{ zIndex: 999 }} className="w-full sm:w-auto">
                        <label className="block text-sm text-gray-400 mb-1">{t('dateRange')}</label>
                        <DatePicker
                            selectsRange={true}
                            startDate={dateRange[0]}
                            endDate={dateRange[1]}
                            onChange={handleDateChange}
                            showMonthYearPicker
                            dateFormat="yyyy-MM"
                            minDate={new Date(new Date().getTime() - maxRangeMonths * 30 * 24 * 60 * 60 * 1000)}
                            maxDate={new Date()}
                            className="bg-gray-800 border border-gray-700 rounded px-4 py-2.5 w-full sm:w-64 text-white cursor-pointer"
                            placeholderText="Select month range"
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                        />
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleApply}
                            disabled={loading || !companyId}
                            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-medium transition ${loading || !companyId ? 'bg-gray-700 cursor-not-allowed' : 'bg-sky-600 hover:bg-blue-700'
                                }`}
                        >
                            <FunnelIcon className="w-5 h-5" />
                            <span>{t('apply')}</span>
                        </button>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleExportExcel}
                            disabled={exporting}
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors duration-200 ${exporting ? 'bg-gray-700 text-gray-300 cursor-not-allowed' : 'bg-sky-600 text-white hover:bg-green-700'
                                }`}
                        >
                            <ArrowDownTrayIcon className="w-5 h-5" />
                            <span>{t('exportToExcel')}</span>
                        </button>
                    </div>
                </div>
            </div>

            {error && (
                <div className="bg-red-900/60 border border-red-700 text-red-100 px-5 py-3 rounded-xl">
                    {error}
                </div>
            )}

            <div className="bg-gray-800 rounded-2xl shadow overflow-hidden">
                <div className="p-5 border-b border-gray-700">
                    <h2 className="text-xl font-semibold">
                        {selectedUserId ? `(${selectedUserId}) ` : ''}{records.length} {t('records')}
                    </h2>
                </div>

                {loading ? (
                    <div className="p-12 text-center text-gray-400">{t('loading')}</div>
                ) : records.length === 0 ? (
                    <div className="p-12 text-center text-gray-500">{t('noDataFound')}</div>
                ) : (
                    <Table
                        data={records}
                        columns={[
                            { header: t('date'), accessor: 'DateLocal', sortable: true, className: "whitespace-nowrap" },
                            { header: t('id'), sortAccessor: (row) => row.user?.systemId || row.UserId, sortable: true, className: "whitespace-nowrap", render: (record) => record.user?.systemId || record.UserId || '—' },
                            { header: t('employee'), sortAccessor: (row) => row.userTitle || `${row.user?.firstName || ''} ${row.user?.lastName || ''}`, sortable: true, className: "whitespace-nowrap", render: (record) => record.userTitle || `${record.user?.firstName || ''} ${record.user?.lastName || ''}`.trim() || '—' },
                            { header: t('totalHours'), sortAccessor: 'minutesWorked', align: 'right', sortable: true, className: "font-medium whitespace-nowrap", render: (record) => formatMinutes(record.minutesWorked) || '—' },
                            { header: t('overtimeHoursShort'), sortAccessor: 'totalOverTimeHours', align: 'right', sortable: true, className: "whitespace-nowrap", render: (record) => record.totalOverTimeHours?.toFixed(1) || '—' },
                            { header: t('avgHours'), sortAccessor: 'averageHours', align: 'right', sortable: true, className: "whitespace-nowrap", render: (record) => record.averageHours?.toFixed(2) || '—' },
                            { header: t('lateDays'), accessor: 'lateDays', align: 'right', sortable: true, className: "whitespace-nowrap", render: (record) => record.lateDays || 0 },
                            { header: t('noDataDays'), accessor: 'noDataDays', align: 'right', sortable: true, className: "whitespace-nowrap", render: (record) => record.noDataDays || 0 },
                            { header: t('totalChargePercentage'), sortAccessor: 'charge.totalChargeForMonth', align: 'right', sortable: true, className: "font-medium whitespace-nowrap", render: (record) => record.charge?.totalChargeForMonth?.toFixed(1) || '—' }
                        ]}
                        defaultSortColumn={0}
                        keyExtractor={(record) => `${record.DateLocal}-${record.UserId}`}
                        containerClassName="max-h-[70vh] overflow-auto"
                        headerClassName="bg-gray-700 sticky top-0 z-10"
                    />
                )}
            </div>
        </div>
    );
}